import type { WorkspaceCreateDto } from '@/types/company'
import { useEffect } from 'preact/hooks'
import { useForm } from 'react-hook-form'
import { FormActions } from '@/components'
import { setFormValidationErrors } from '@/utils/errorHandling'
import { WorkspaceFormFields } from './WorkspaceFormFields'
import type { WorkspaceSaveOutcome } from './WorkspaceCard'
import type { CompanyWebsite } from './workspaceWebsite'

interface WorkspaceEditFormProps {
    workspace: WorkspaceCreateDto
    /** The company's own website, which a workspace created without one uses (ORB-1027). */
    company: CompanyWebsite
    onChange: (workspace: WorkspaceCreateDto) => void
    onSave: () => Promise<WorkspaceSaveOutcome>
    onCancel: () => void
    loading: boolean
    isNew?: boolean
}

type FormValues = {
    Name: string; Slug: string; Website: string; Description: string; Status: string; TimeZone: string; DailyLeadsSummaryEnabled: boolean; DailyLeadsSummaryHour: number
    PostWebinarSummaryEnabled: boolean; StartOfWeekSummaryEnabled: boolean; StartOfWeekSummaryDayOfWeek: number; StartOfWeekSummaryHour: number
}

/**
 * The form's view of the draft. Extracted because it is needed twice — as `defaultValues` and again
 * by the mount `reset` — and a field added to only one copy is halfway to the bug this file already
 * shipped once: Slug was rendered, derived and validated, then dropped on the way back out.
 */
const toFormValues = (ws: WorkspaceCreateDto): FormValues => ({
    Name: ws.Name || '',
    Slug: ws.Slug || '',
    Website: ws.Website || '',
    Description: ws.Description || '',
    Status: ws.Status || 'Active',
    TimeZone: ws.TimeZone || '',
    DailyLeadsSummaryEnabled: ws.DailyLeadsSummaryEnabled ?? false,
    DailyLeadsSummaryHour: ws.DailyLeadsSummaryHour ?? 8,
    PostWebinarSummaryEnabled: ws.PostWebinarSummaryEnabled ?? false,
    StartOfWeekSummaryEnabled: ws.StartOfWeekSummaryEnabled ?? false,
    StartOfWeekSummaryDayOfWeek: ws.StartOfWeekSummaryDayOfWeek ?? 1,
    StartOfWeekSummaryHour: ws.StartOfWeekSummaryHour ?? 8,
})

export function WorkspaceEditForm({ workspace, company, onChange, onSave, onCancel, loading, isNew }: WorkspaceEditFormProps) {
    const { register, control, handleSubmit, watch, setValue, setError, formState: { errors, isValid, isDirty }, reset } = useForm<FormValues>({
        mode: 'onChange',
        defaultValues: toFormValues(workspace),
    })

    // Initialize form once; avoid resetting on each external state echo so user edits & errors persist
    useEffect(() => {
        reset(toFormValues(workspace))
    }, [])

    // Push changes upward so parent keeps draft state (maintain existing API).
    //
    // Slug is watched like any other field, but it is the one nobody types: WorkspaceFormFields
    // derives it from the name inside SlugInput and writes it back with setValue, against a hidden
    // required registration. Leaving it out of this list is invisible — the field renders, the slug
    // appears in it, and the form goes valid — while the draft handed to the parent keeps the empty
    // slug it started with and the server rejects the create. ORB-570.
    const wName = watch('Name')
    const wSlug = watch('Slug')
    const wWebsite = watch('Website')
    const wDesc = watch('Description')
    const wStatus = watch('Status')
    const wTimeZone = watch('TimeZone')
    const wDailyLeads = watch('DailyLeadsSummaryEnabled')
    const wDailyHour = watch('DailyLeadsSummaryHour')
    const wPostWebinar = watch('PostWebinarSummaryEnabled')
    const wStartOfWeek = watch('StartOfWeekSummaryEnabled')
    const wStartOfWeekDay = watch('StartOfWeekSummaryDayOfWeek')
    const wStartOfWeekHour = watch('StartOfWeekSummaryHour')
    useEffect(() => {
        onChange({
            ...workspace,
            Name: wName,
            Slug: wSlug,
            Website: wWebsite.trim() || null,
            Description: wDesc,
            Status: wStatus,
            TimeZone: wTimeZone,
            DailyLeadsSummaryEnabled: wDailyLeads,
            DailyLeadsSummaryHour: Number(wDailyHour),
            PostWebinarSummaryEnabled: wPostWebinar,
            StartOfWeekSummaryEnabled: wStartOfWeek,
            StartOfWeekSummaryDayOfWeek: Number(wStartOfWeekDay),
            StartOfWeekSummaryHour: Number(wStartOfWeekHour),
        })
    }, [wName, wSlug, wWebsite, wDesc, wStatus, wTimeZone, wDailyLeads, wDailyHour, wPostWebinar, wStartOfWeek, wStartOfWeekDay, wStartOfWeekHour])

    const submit = async () => {
        if (!isValid) return
        const outcome = await onSave()
        if (!outcome.ok) setFormValidationErrors(outcome, setError)
    }

    return (
        <form onSubmit={handleSubmit(submit)} className="space-y-4">
            <h4 className="font-medium text-slate-900 dark:text-slate-100">{isNew ? 'Add New Workspace' : 'Edit Workspace'}</h4>
            <WorkspaceFormFields control={control} register={register} errors={errors} watch={watch} setValue={setValue} website={company} />
            <FormActions
                isSubmitting={loading}
                isSaveDisabled={!isValid || !isDirty}
                onCancel={onCancel}
                saveText={isNew ? 'Add Workspace' : 'Save Changes'}
                className="px-0 py-1 border-0 bg-transparent dark:bg-transparent"
            />
        </form>
    )
}
